import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";

import {
  StoryReviewFormData,
  StoryWorkflowFormData,
} from "@cms/utils/forms/story";
import { getEditorStoryVersion } from "@cms/utils/helpers";
import {
  generateIdempotencyKey,
  ISystemResponse,
  ISystemStory,
  ISystemStoryVersion,
  ISystemStoryVersionReview,
} from "@dl/declarations";
import api from "../../api";
import { IUpdateStoryDto } from "../../interfaces/dto.interface";
import { ActionPayloadType, TEditorStoryVersion } from "../../types";

export interface IEditorState {
  isGeneralSettingsOpen: boolean;
  mode: "editing" | "readonly";
  mode_status: "idle" | "requesting-edit" | "transferring-edit";
  state: "idle" | "fetching" | "saving" | "lock-changing" | "workflow-updating";

  idempotencyKey: string | null;
  story: TEditorStoryVersion | null;

  versions: Array<ISystemStoryVersion>;
  currentVersion: ISystemStoryVersion | null;
  lastSavedVersionId: string | null;
  lastPublishedVersionId: string | null;

  hasContentElementsChanged: boolean;
  hasContentElementsLoaded: boolean;

  lastActivityTimestamp: number;
}

const initialState: IEditorState = {
  isGeneralSettingsOpen: true,
  state: "idle",
  mode: "editing",
  mode_status: "idle",

  idempotencyKey: generateIdempotencyKey(),
  story: null,

  versions: [],
  currentVersion: null,
  lastSavedVersionId: null,
  lastPublishedVersionId: null,

  hasContentElementsChanged: false,
  hasContentElementsLoaded: false,

  lastActivityTimestamp: Date.now(),
};

export const getStoryAsyncAction = createAsyncThunk<
  ISystemStory,
  { id: string }
>("editor/getStory", async ({ id }) => {
  const response = await api.get<ISystemStory>(`/stories/${id}`);
  return response.data;
});

export const saveStoryAsyncAction = createAsyncThunk<
  ISystemStory,
  { id?: string; dto: IUpdateStoryDto }
>("editor/saveStory", async ({ id, dto }) => {
  if (id) {
    const response = await api.put<ISystemStory>(`/stories/${id}/save`, dto);
    return response.data;
  }

  const response = await api.post<ISystemStory>(`/stories`, dto);
  return response.data;
});

export const reviewStoryVersionAsyncAction = createAsyncThunk<
  { id: string; reviews: ISystemStoryVersionReview["reviews"] },
  { id: string; data: StoryReviewFormData }
>("editor/reviewStoryVersion", async ({ id, data }) => {
  const response = await api.put<ISystemStoryVersionReview>(
    `/stories/versions/${id}/review`,
    data,
  );
  return { reviews: response.data.reviews, id };
});

export const updateStoryWorkflowAsyncAction = createAsyncThunk<
  ISystemStory,
  { id: string; data: StoryWorkflowFormData }
>("editor/updateStoryWorkflow", async ({ id, data }) => {
  const response = await api.put<ISystemStory>(`/stories/${id}/workflow`, data);
  return response.data;
});

export const cancelStoryWorkflowAsyncAction = createAsyncThunk<
  ISystemStory,
  { id: string }
>("editor/cancelStoryWorkflow", async ({ id }) => {
  const response = await api.delete<ISystemStory>(`/stories/${id}/workflow`);
  return response.data;
});

export const getAllStoryVersionsAsyncAction = createAsyncThunk<
  ISystemStoryVersion[],
  { id: string }
>("editor/getAllStoryVersions", async ({ id }) => {
  const response = await api.get<ISystemResponse<ISystemStoryVersion[]>>(
    `/stories/${id}/versions`,
  );
  return response.data.data || [];
});

export const getSpecificStoryVersionAsyncAction = createAsyncThunk<
  ISystemStoryVersion,
  { storyId: string; versionId: string }
>(
  "editor/getSpecificStoryVersion",
  async ({ storyId, versionId }, { getState }) => {
    const state = getState() as { editor: IEditorState };

    const versions = state.editor.versions;

    const existingVersion = versions.find((v) => v._id === versionId);

    if (existingVersion && "__v" in existingVersion) {
      return existingVersion;
    }

    const response = await api.get<ISystemStoryVersion>(
      `/stories/${storyId}/versions/${versionId}`,
    );

    return response.data;
  },
);

const editorSlice = createSlice({
  name: "editor",
  initialState,
  reducers: {
    toggleIsGeneralSettingsOpenAction: (
      state,
      action: ActionPayloadType<boolean>,
    ) => {
      return {
        ...state,
        isGeneralSettingsOpen: action.payload,
      };
    },

    setEditorState: (
      state,
      action: ActionPayloadType<Partial<IEditorState>>,
    ) => {
      return {
        ...state,
        ...action.payload,
      };
    },

    resetEditorState: () => {
      return {
        ...initialState,
        idempotencyKey: generateIdempotencyKey(),
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getAllStoryVersionsAsyncAction.fulfilled,
      (state, action) => {
        return {
          ...state,
          versions: action.payload,
        };
      },
    );

    builder.addCase(
      getSpecificStoryVersionAsyncAction.fulfilled,
      (state, action) => {
        const newCurrentVersion = action.payload;
        const lastSavedVersionId = state.lastSavedVersionId;

        const versions = state.versions.map((version) => {
          if (version._id === newCurrentVersion._id) {
            return newCurrentVersion;
          }

          return version;
        });

        const story = state.story;

        // const usedStory = getEditorStoryVersion({
        //   ...(newCurrentVersion._id === lastSavedVersionId
        //     ? story
        //     : {
        //         _id: story?._id,
        //         slug: story?.slug,
        //         is_locked: story?.is_locked,

        //         locked_by: story?.locked_by,
        //         lockedByObject: story?.lockedByObject,
        //         published_story_version: story?.published_story_version,
        //         publishedStoryVersionObject: story?.publishedStoryVersionObject,

        //         workflow: story?.story_workflow,

        //         collectionsObjects: story?.collectionsObjects,

        //         created_at: story?.story_created_at,
        //         updated_at: story?.story_updated_at,
        //         publish_date: story?.story_publish_date,
        //         first_publish_date: story?.story_first_publish_date,
        //         display_date: story?.story_display_date,
        //       }),
        //   saved_story_version: newCurrentVersion._id,
        //   savedStoryVersionObject: newCurrentVersion,
        // } as ISystemStory);

        const usedStory =
          newCurrentVersion._id === lastSavedVersionId
            ? story
            : getEditorStoryVersion({
              _id: story?._id,
              slug: story?.slug,
              is_locked: story?.is_locked,

              locked_by: story?.locked_by,
              lockedByObject: story?.lockedByObject,
              published_story_version: story?.published_story_version,
              publishedStoryVersionObject: story?.publishedStoryVersionObject,

              workflow: story?.story_workflow,

              collectionsObjects: story?.collectionsObjects,

              created_at: story?.story_created_at,
              updated_at: story?.story_updated_at,
              publish_date: story?.story_publish_date,
              first_publish_date: story?.story_first_publish_date,
              display_date: story?.story_display_date,
              saved_story_version: newCurrentVersion._id,
              savedStoryVersionObject: newCurrentVersion,
            } as ISystemStory);

        let hasContentElementsChanged = false;

        if (newCurrentVersion._id !== lastSavedVersionId) {
          hasContentElementsChanged = true;
        }

        return {
          ...state,
          state: "idle",
          versions,
          story: usedStory,
          currentVersion: newCurrentVersion,
          hasContentElementsChanged,
        };
      },
    );

    builder.addCase(
      reviewStoryVersionAsyncAction.fulfilled,
      (state, action) => {
        const { id: storyVersionId, reviews: storyVersionReviews } =
          action.payload;

        const versions = state.versions.map((version) => {
          if (version._id === storyVersionId) {
            return {
              ...version,
              reviews: storyVersionReviews,
            };
          }

          return version;
        });

        let usedCurrentVersion = state.currentVersion;
        if (usedCurrentVersion && usedCurrentVersion._id === storyVersionId) {
          usedCurrentVersion = {
            ...usedCurrentVersion,
            reviews: storyVersionReviews,
          };
        }

        let usedStory = state.story;

        if (usedStory && state.currentVersion?._id === storyVersionId) {
          usedStory = {
            ...usedStory,
            savedStoryVersionObject: {
              ...usedStory.savedStoryVersionObject!,
              reviews: storyVersionReviews,
            },
          };
        }

        return {
          ...state,
          state: "idle",
          versions,
          story: usedStory,
          currentVersion: usedCurrentVersion,
        };
      },
    );

    builder.addMatcher(
      isAnyOf(saveStoryAsyncAction.fulfilled),
      (state, action) => {
        const story = action.payload;
        const storyVersion = story.savedStoryVersionObject!;

        const versions =
          storyVersion._id !== state.lastSavedVersionId
            ? [storyVersion, ...state.versions]
            : state.versions;

        const usedStory = getEditorStoryVersion(story);

        const lastSavedVersionId = story?.saved_story_version || null;
        const lastPublishedVersionId = story?.published_story_version || null;

        return {
          ...state,
          versions,
          state: "idle",
          story: usedStory,
          currentVersion: storyVersion,
          hasContentElementsChanged: false,
          lastActivityTimestamp: Date.now(),

          lastSavedVersionId,
          lastPublishedVersionId,
        };
      },
    );

    builder.addMatcher(
      isAnyOf(
        getStoryAsyncAction.fulfilled,
        updateStoryWorkflowAsyncAction.fulfilled,
        cancelStoryWorkflowAsyncAction.fulfilled,
      ),
      (state, action) => {
        const story = action.payload;
        const storyVersion = story.savedStoryVersionObject!;

        const versions = state.versions.map((version) => {
          if (version._id === storyVersion?._id) {
            return storyVersion;
          }

          return version;
        });

        const usedStory = getEditorStoryVersion(story);

        const lastSavedVersionId = story?.saved_story_version || null;
        const lastPublishedVersionId = story?.published_story_version || null;

        return {
          ...state,
          versions,
          state: "idle",
          story: usedStory,
          currentVersion: storyVersion,
          hasContentElementsChanged: false,
          lastActivityTimestamp: Date.now(),

          lastSavedVersionId,
          lastPublishedVersionId,
        };
      },
    );

    builder.addMatcher(
      isAnyOf(
        getStoryAsyncAction.pending,
        getSpecificStoryVersionAsyncAction.pending,
      ),
      (state) => {
        return {
          ...state,
          state: "fetching",
        };
      },
    );

    builder.addMatcher(
      isAnyOf(
        saveStoryAsyncAction.pending,
        reviewStoryVersionAsyncAction.pending,
      ),
      (state) => {
        return {
          ...state,
          state: "saving",
        };
      },
    );

    builder.addMatcher(
      isAnyOf(
        updateStoryWorkflowAsyncAction.pending,
        cancelStoryWorkflowAsyncAction.pending,
      ),
      (state) => {
        return {
          ...state,
          state: "workflow-updating",
        };
      },
    );

    builder.addMatcher(
      isAnyOf(
        getStoryAsyncAction.rejected,
        saveStoryAsyncAction.rejected,
        reviewStoryVersionAsyncAction.rejected,
        updateStoryWorkflowAsyncAction.rejected,
        cancelStoryWorkflowAsyncAction.rejected,
        getSpecificStoryVersionAsyncAction.rejected,
      ),
      (state) => {
        return {
          ...state,
          state: "idle",
        };
      },
    );
  },
});

export const {
  toggleIsGeneralSettingsOpenAction,
  resetEditorState,
  setEditorState,
} = editorSlice.actions;

export default editorSlice.reducer;
